import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  MeetingRoom as MeetingRoomIcon,
  Phone as PhoneIcon,
  People as PeopleIcon,
  EventAvailable as EventAvailableIcon,
  WarningAmber as WarningAmberIcon,
  Block as BlockIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { listarSalas, Sala } from '../../services/salaService';
import SkeletonLoader from '../common/SkeletonLoader';
import { useTheme } from '../../contexts/ThemeContext';
import { corSalaParaTexto } from '../../utils/corSala';

const ListaSalas: React.FC = () => {
  const navigate = useNavigate();
  const { tema } = useTheme();
  const corTexto = (cor?: string) => corSalaParaTexto(cor || '#3498db', tema === 'escuro');
  const [salas, setSalas] = useState<Sala[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [salaAvisoId, setSalaAvisoId] = useState<string | null>(null);

  useEffect(() => {
    carregarSalas();
  }, []);

  const carregarSalas = async () => {
    try {
      setCarregando(true);
      setErro('');
      const dados = await listarSalas(true);
      setSalas(dados);
    } catch (error: any) {
      console.error('Erro ao carregar salas:', error);
      setErro(error.response?.data?.error || 'Erro ao carregar salas');
    } finally {
      setCarregando(false);
    }
  };

  const handleReservar = (sala: Sala) => {
    if (sala.tipo === 'Reunião') {
      setSalaAvisoId(sala.id);
    } else {
      navigate(`/reservas/nova/${sala.id}`);
    }
  };

  const confirmarReservaReuniao = () => {
    if (salaAvisoId) navigate(`/reservas/nova/${salaAvisoId}`);
    setSalaAvisoId(null);
  };

  const handleVerCalendario = (salaId: string) => {
    navigate(`/reservas/calendario/${salaId}`);
  };

  const getIconeSala = (tipo: string) => {
    return tipo === 'Reunião' ? <MeetingRoomIcon /> : <PhoneIcon />;
  };

  if (carregando) {
    return <SkeletonLoader />;
  }

  if (erro) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{erro}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Reserva de Salas
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Selecione uma sala para fazer sua reserva
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {salas.map((sala) => (
          <Box key={sala.id} sx={{ width: { xs: '100%', md: 'calc(50% - 12px)', lg: 'calc(33.333% - 16px)' } }}>
            <Card 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderLeft: `4px solid ${sala.cor}`,
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      bgcolor: `${sala.cor}20`,
                      color: corTexto(sala.cor),
                      mr: 2
                    }}
                  >
                    {getIconeSala(sala.tipo)}
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" gutterBottom>
                      {sala.nome}
                    </Typography>
                    <Chip
                      label={sala.tipo}
                      size="small"
                      sx={{
                        bgcolor: `${sala.cor}20`,
                        color: corTexto(sala.cor),
                        fontWeight: 'bold'
                      }}
                    />
                  </Box>
                </Box>

                {sala.descricao && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {sala.descricao}
                  </Typography>
                )}


                <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={sala.nome === 'Sala Cruzeiro' ? <BlockIcon /> : <EventAvailableIcon />}
                    onClick={() => sala.nome !== 'Sala Cruzeiro' && handleReservar(sala)}
                    disabled={sala.nome === 'Sala Cruzeiro'}
                    sx={{
                      bgcolor: sala.nome === 'Sala Cruzeiro' ? '#9e9e9e' : sala.cor,
                      '&:hover': {
                        bgcolor: sala.nome === 'Sala Cruzeiro' ? '#9e9e9e' : sala.cor,
                        filter: sala.nome === 'Sala Cruzeiro' ? 'none' : 'brightness(0.9)'
                      },
                      '&.Mui-disabled': { bgcolor: '#bdbdbd', color: '#fff' }
                    }}
                  >
                    {sala.nome === 'Sala Cruzeiro' ? 'Indisponível' : 'Reservar'}
                  </Button>
                  {sala.nome !== 'Sala Cruzeiro' && (
                    <Button
                      variant="outlined"
                      onClick={() => handleVerCalendario(sala.id)}
                      sx={{
                        borderColor: corTexto(sala.cor),
                        color: corTexto(sala.cor),
                        '&:hover': {
                          borderColor: corTexto(sala.cor),
                          bgcolor: `${sala.cor}10`
                        }
                      }}
                    >
                      Ver Agenda
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      {salas.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            Nenhuma sala disponível no momento
          </Typography>
        </Box>
      )}

      <Dialog open={!!salaAvisoId} onClose={() => setSalaAvisoId(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon sx={{ color: 'warning.main' }} />
          Atenção — Sala de Reunião
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1 }}>
            A <strong>Sala de Reunião</strong> deve ser reservada exclusivamente para reuniões <strong>presenciais com duas ou mais pessoas</strong>.
          </Typography>
          <Typography variant="body1">
            Para reuniões ou calls individuais, utilize a <strong>Sala Atlético</strong> ou a <strong>Sala Cruzeiro</strong>.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button variant="outlined" onClick={() => setSalaAvisoId(null)}>
            Cancelar
          </Button>
          <Button variant="contained" color="error" onClick={confirmarReservaReuniao}>
            Entendi, continuar com a reserva
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ListaSalas;
